import { SystemStyleObject, theme } from '@chakra-ui/react';
import type { ComponentStyleConfig } from '@chakra-ui/theme';
import { StyleFunctionProps } from '@chakra-ui/theme-tools';

const getVisuallyDisabledStyles = (
  disabledStyles: SystemStyleObject,
  hoverDisabledStyles?: SystemStyleObject
): SystemStyleObject => ({
  '&[data-visually-disabled]': {
    ...disabledStyles,
    cursor: 'pointer',
    _active: hoverDisabledStyles ?? disabledStyles,
    _hover: hoverDisabledStyles ?? disabledStyles,
  },
});

const primary = (props: StyleFunctionProps) => {
  const disabledStyles: SystemStyleObject = {
    bg: 'neutral.fill-subtle',
    color: 'neutral.fg-disabled',
    opacity: 1,
  };

  return {
    ...theme.components.Button.variants?.solid(props),
    bg: 'brand.solid-primary',
    color: 'misc.white-fix-primary',
    borderColor: 'transparent',
    _hover: {
      bg: ['brand.solid-primary', 'brand.solid-hover'],
      _disabled: disabledStyles,
    },
    _active: {
      bg: 'brand.solid-hover',
      color: 'misc.white-fix-secondary',
    },
    _disabled: disabledStyles,
    ...getVisuallyDisabledStyles(disabledStyles),
  };
};

const secondary = (props: StyleFunctionProps) => {
  const disabledStyles: SystemStyleObject = {
    bg: 'transparent',
    color: 'neutral.fg-disabled',
    borderColor: 'neutral.border-base',
    opacity: 1,
  };

  return {
    ...theme.components.Button.variants?.outline(props),
    bg: 'transparent',
    color: 'neutral.fg-primary',
    borderColor: 'neutral.border-base',
    _hover: {
      bg: 'neutral.fill-subtle',
      color: 'neutral.fg-primary',
      borderColor: 'transparent',
      _disabled: disabledStyles,
    },
    _active: {
      bg: 'neutral.fill-active',
      color: 'neutral.fg-secondary',
      borderColor: 'transparent',
    },
    _disabled: disabledStyles,
    ...getVisuallyDisabledStyles(disabledStyles),
  };
};

// naked is the canonical name; tertiary is kept as a legacy alias
const naked = (props: StyleFunctionProps) => {
  const disabledStyles: SystemStyleObject = {
    color: 'neutral.fg-disabled',
    opacity: 1,
  };

  return {
    ...theme.components.Button.variants?.outline(props),
    px: '0',
    border: '0',
    minW: 'auto',
    color: 'brand.fg-primary',
    _hover: {
      color: 'brand.fg-secondary',
      _disabled: disabledStyles,
    },
    _active: {
      color: 'brand.fg-secondary',
    },
    _disabled: disabledStyles,
    ...getVisuallyDisabledStyles(disabledStyles),
  };
};

const negative = (props: StyleFunctionProps) => {
  const disabledStyles: SystemStyleObject = {
    bg: 'neutral.fill-subtle',
    color: 'neutral.fg-disabled',
    opacity: 1,
  };

  return {
    ...theme.components.Button.variants?.solid(props),
    bg: 'negative.solid-primary',
    color: 'misc.white-fix-primary',
    borderColor: 'transparent',
    _hover: {
      bg: 'negative.fg-secondary',
      _disabled: disabledStyles,
    },
    _active: {
      bg: 'negative.fg-secondary',
      color: 'misc.white-fix-secondary',
    },
    _disabled: disabledStyles,
    ...getVisuallyDisabledStyles(disabledStyles),
  };
};

const brandSecondary = (_props: StyleFunctionProps) => {
  const disabledStyles: SystemStyleObject = {
    bg: 'neutral.fill-subtle',
    color: 'neutral.fg-disabled',
    opacity: 1,
  };

  return {
    bg: 'brand.fill-subtle',
    color: 'brand.fg-primary',
    border: '0',
    _hover: {
      bg: 'brand.fill-hover',
      color: 'brand.fg-primary',
      _disabled: disabledStyles,
    },
    _active: {
      bg: 'brand.fill-hover',
      color: 'brand.fg-secondary',
    },
    _disabled: disabledStyles,
    ...getVisuallyDisabledStyles(disabledStyles),
  };
};

const neutralSecondary = (_props: StyleFunctionProps) => {
  const disabledStyles: SystemStyleObject = {
    bg: 'neutral.fill-subtle',
    color: 'neutral.fg-disabled',
    opacity: 1,
  };

  return {
    bg: 'neutral.fill-subtle',
    color: 'neutral.fg-primary',
    border: '0',
    _hover: {
      bg: 'neutral.fill-active',
      color: 'neutral.fg-primary',
      _disabled: disabledStyles,
    },
    _active: {
      bg: 'neutral.fill-active',
      color: 'neutral.fg-secondary',
    },
    _disabled: disabledStyles,
    ...getVisuallyDisabledStyles(disabledStyles),
  };
};

const negativeSecondary = (_props: StyleFunctionProps) => {
  const disabledStyles: SystemStyleObject = {
    bg: 'neutral.fill-subtle',
    color: 'neutral.fg-disabled',
    opacity: 1,
  };

  return {
    bg: 'negative.bg-primary',
    color: 'negative.fg-primary',
    border: '0',
    _hover: {
      bg: 'negative.bg-secondary',
      color: 'negative.fg-primary',
      _disabled: disabledStyles,
    },
    _active: {
      bg: 'negative.bg-secondary',
      color: 'negative.fg-secondary',
    },
    _disabled: disabledStyles,
    ...getVisuallyDisabledStyles(disabledStyles),
  };
};

const unstyled = (_props: StyleFunctionProps) => ({
  ...theme.components.Button.variants?.unstyled,
  borderStyle: 'none',
});

const link = () => ({
  ...theme.components.Button.variants?.link,
  borderStyle: 'none',
});

const ghost = () => ({
  ...theme.components.Button.variants?.ghost,
  borderStyle: 'none',
});

export const styleConfig: ComponentStyleConfig = {
  baseStyle: {
    fontWeight: 'bold',
    height: 'auto',
    border: '0.1',
    borderRadius: 'lg',
    _focusVisible: {
      outline: 0,
      boxShadow: '0 0 0 1px white, 0 0 0 3px #7DA9FF',
    },
  },
  sizes: {
    // Canonical sizes
    xl: {
      fontSize: 'md',
      lineHeight: 'tiny',
      h: '14',
      minW: 'auto',
      py: '4',
      px: '4',
    },
    lg: {
      fontSize: 'md',
      fontWeight: 'semibold',
      lineHeight: 'short',
      h: '12',
      minW: 'auto',
      py: '3',
      px: '4',
    },
    md: {
      py: '2',
      px: '2',
      h: '9',
      minW: 'auto',
      fontSize: 'md',
      lineHeight: 'none',
    },
    sm: {
      py: '1.5',
      px: '2',
      h: '7',
      minW: 'auto',
      fontSize: 'sm-md',
      fontWeight: 'semibold',
      lineHeight: 'none',
      borderRadius: 'md',
    },
    // Legacy sizes (deprecated — use xl, lg, md instead)
    default: {
      fontSize: 'md',
      fontWeight: 'semibold',
      lineHeight: 'short',
      h: '12',
      py: '3',
      px: '4',
    },
    large: {
      fontSize: 'md',
      lineHeight: 'tiny',
      h: '14',
      py: '4',
      px: '4',
    },
    small: {
      py: '2',
      px: '2',
      h: '9',
      fontSize: 'md',
      lineHeight: 'none',
    },
  },
  variants: {
    primary,
    secondary,
    'brand-secondary': brandSecondary,
    'neutral-secondary': neutralSecondary,
    naked,
    /** @deprecated Use `naked` instead. */
    tertiary: naked,
    negative,
    'negative-secondary': negativeSecondary,
    unstyled,
    link,
    ghost,
  },
  defaultProps: {
    variant: 'primary',
    size: 'lg',
  },
};
